import { Division } from "../../../domain/divisions/division";
import { roundToPrecision } from "../../../helpers/math";
import { hasValue } from "../../../helpers/nullable";
import { centsToDollarsDisplay } from "../../displayFormatters";
import { StatisticItem } from "../ReportStatistics";
import { EstimateDetailedCloseData, EstimateDetailedTurnaroundData, EstimateReportData, EstimatesSummary } from "./estimatesReportApi";

export const getUniqueSalesPersonNames = (estimatesSummary: EstimatesSummary): string[] => {
  const salesPersonNames = new Set<string>();

  Object.values(estimatesSummary).forEach(estimateList => {
    estimateList.forEach((estimate: EstimateReportData) => {
      salesPersonNames.add(estimate.salesPersonName);
    });
  });

  return Array.from(salesPersonNames);
};

export const getUniqueDivisionNames = (divisions: Division[]): string[] => {
  const divisionNames = new Set<string>();

  divisions.forEach(division => {
    divisionNames.add(division.name);
  });

  return Array.from(divisionNames);
};

export const getFilteredEstimatesData = (estimatesData: EstimatesSummary, salesPersonIds: string[], projectTypeIds: number[], divisionIds: string[], searchTerm: string, archived: boolean = false) => {
  const applyFilters = (list: EstimateReportData[]) =>
    list.filter((estimate) =>
      hasValue(estimate.archivedAtUtc) === archived &&
      (salesPersonIds.length === 0 || salesPersonIds.includes(estimate.salesPersonName)) &&
      (projectTypeIds.length === 0 || projectTypeIds.includes(estimate.jobType)) &&
      (divisionIds === undefined || divisionIds === null || divisionIds.length === 0 || 
        (estimate.divisions && divisionIds.map(id => parseInt(id, 10)).some(id => 
          estimate.divisions.split('|').map(d => parseInt(d, 10)).includes(id)
        ))) &&
      (searchTerm === '' || 
        (estimate.jobName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (estimate.jobNo?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (estimate.displayName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        `${estimate.address1 || ''}, ${estimate.city || ''}, ${estimate.state || ''}, ${estimate.zip || ''}`.toLowerCase().includes(searchTerm.toLowerCase()))
    );

  return {
    unsent: applyFilters(estimatesData.unsent),
    sent: applyFilters(estimatesData.sent),
    approved: applyFilters(estimatesData.approved),
    lost: applyFilters(estimatesData.lost)
  };
}

export const getFilteredDetailedCloseData = (detailedCloseData: EstimateDetailedCloseData[], salesPersonIds: string[], projectTypeIds: number[], divisionIds: string[]) => {
  console.log(detailedCloseData)
  const filtered = detailedCloseData.filter((estimate) =>
    (salesPersonIds.length === 0 || salesPersonIds.includes(estimate.salesPersonName)) &&
    (projectTypeIds.length === 0 || projectTypeIds.includes(estimate.jobType)) &&
    (divisionIds.length === 0 || divisionIds.includes(estimate.divisionName))
  );

  console.log(filtered) 

  return{
    totalDaysToClose: filtered.reduce((acc, { daysToClose }) => acc + daysToClose, 0),
    estimateTotal: filtered.length,
    daysToClose: filtered.reduce((acc, { daysToClose }) => acc + daysToClose, 0) / filtered.length
  }
}

export const getFilteredDetailedTurnaroundData = (detailedTurnaroundData: EstimateDetailedTurnaroundData[], salesPersonIds: string[], projectTypeIds: number[], divisionIds: string[]) => {
  const filtered = detailedTurnaroundData.filter((estimate) =>
    (salesPersonIds.length === 0 || salesPersonIds.includes(estimate.salesPersonName)) &&
    (projectTypeIds.length === 0 || projectTypeIds.includes(estimate.jobType)) &&
    (divisionIds.length === 0 || divisionIds.includes(estimate.divisionName))
  )

  return{
    totalEstimateTurnaroundDays: filtered.reduce((acc, { estimateTurnaround }) => acc + estimateTurnaround, 0),
    estimateTotal: filtered.length,
    estimateTurnaround: filtered.reduce((acc, { estimateTurnaround }) => acc + estimateTurnaround, 0) / filtered.length
  }
}

export type EstimateSummaryStats = {
  totalEstimatesUnsent: number;
  totalEstimatesSent: number;
  totalEstimatesApproved: number;
  totalEstimatesLost: number;
  totalPriceUnsent: number;
  totalPriceSent: number;
  totalPriceApproved: number;
  totalPriceLost: number;
  closeRateByNumberOf: number;
  closeRateByDollars: number;
}

export const getEstimateSummaryStats = (estimate: EstimatesSummary): EstimateSummaryStats => ({
  totalEstimatesUnsent: estimate.unsent.length,
  totalEstimatesSent: estimate.sent.length,
  totalEstimatesApproved: estimate.approved.length,
  totalEstimatesLost: estimate.lost.length,
  totalPriceUnsent: estimate.unsent.reduce((acc, { price }) => acc + price, 0),
  totalPriceSent: estimate.sent.reduce((acc, { price }) => acc + price, 0),
  totalPriceApproved: estimate.approved.reduce((acc, { price }) => acc + price, 0),
  totalPriceLost: estimate.lost.reduce((acc, { price }) => acc + price, 0),
  closeRateByNumberOf:
    estimate.sent.length > 0
      ? roundToPrecision(
        (estimate.approved.length / estimate.sent.length) * 100,
        2
      )
      : 0,
  closeRateByDollars:
    estimate.sent.length > 0
      ? roundToPrecision(
        (estimate.approved.reduce((acc, { price }) => acc + price, 0) / estimate.sent.reduce((acc, { price }) => acc + price, 0)) * 100,
        2
      )
      : 0,
});

export const getEstimateStatistics = (
  estimates: EstimatesSummary, 
): StatisticItem[] => {
  const summaryStats = getEstimateSummaryStats(estimates);
  return [
    {
      title: 'Total Drafts',
      value: summaryStats.totalEstimatesUnsent,
      extraFinancialData: centsToDollarsDisplay(summaryStats.totalPriceUnsent) || '',
    },
    {
      title: 'Total Sent',
      value: summaryStats.totalEstimatesSent,
      extraFinancialData: centsToDollarsDisplay(summaryStats.totalPriceSent) || '',
    },
    {
      title: 'Total Approved',
      value: summaryStats.totalEstimatesApproved,
      extraFinancialData: centsToDollarsDisplay(summaryStats.totalPriceApproved) || '',
    },
    {
      title: 'Total Lost',
      value: summaryStats.totalEstimatesLost,
      extraFinancialData: centsToDollarsDisplay(summaryStats.totalPriceLost) || '',
    },
  ];
}
